import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { setPrebuiltWorkflow, setTextToWorkflowPrompt } from "@/redux/User";
import {
  PREBUILT_WORKFLOWS,
  type PrebuiltWorkflow,
} from "@/constants/prebuiltWorkflows";
import { axiosGet } from "@/services/axiosService";

const T = {
  font: "'Rubik', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  coral: "#FB5F66",
  coralDark: "#e3232c",
  text: "#1a1a2e",
  textSecondary: "#6b7280",
  textMuted: "#9ca3af",
  bg: "#fafafa",
  card: "#fff",
  border: "#eef0f4",
};

const NODE_META: Record<string, { icon: string; label: string; desc: string }> =
  {
    campaign: {
      icon: "▶",
      label: "Trigger",
      desc: "Starts the workflow when an event occurs",
    },
    hitApi: {
      icon: "⚡",
      label: "API Call",
      desc: "Connects to an external service",
    },
    sendEmail: {
      icon: "✉",
      label: "Email",
      desc: "Sends an email notification",
    },
    sendSMS: { icon: "💬", label: "SMS", desc: "Sends a text message" },
    condition: {
      icon: "◇",
      label: "Condition",
      desc: "Checks a condition and branches",
    },
    delayAction: {
      icon: "⏱",
      label: "Delay",
      desc: "Waits before proceeding",
    },
    runCode: {
      icon: "{ }",
      label: "Code",
      desc: "Runs custom JavaScript logic",
    },
    sendMessage: { icon: "💭", label: "Message", desc: "Sends a chat message" },
    approvalGate: {
      icon: "✋",
      label: "Approval",
      desc: "Waits for human approval",
    },
    reviewLoop: {
      icon: "🔄",
      label: "Review Loop",
      desc: "Iterative review cycle",
    },
    whatsApp: {
      icon: "📱",
      label: "WhatsApp",
      desc: "Sends a WhatsApp message",
    },
    pushNotification: {
      icon: "🔔",
      label: "Push",
      desc: "Sends a push notification",
    },
    linkedIn: { icon: "💼", label: "LinkedIn", desc: "LinkedIn action" },
    abTest: {
      icon: "🔀",
      label: "A/B Test",
      desc: "Splits traffic for testing",
    },
  };

// Detect services from node URLs/labels
function detectServices(nodes: any[]): string[] {
  const services = new Set<string>();
  for (const n of nodes) {
    const url = n.data?.hitApiDetails?.url || n.config?.url || "";
    const label = (n.data?.nodeLabel || n.title || "").toLowerCase();
    const combined = url.toLowerCase() + " " + label;
    if (combined.includes("openai") || combined.includes("gpt"))
      services.add("OpenAI");
    else if (combined.includes("anthropic") || combined.includes("claude"))
      services.add("Anthropic");
    else if (combined.includes("openrouter")) services.add("OpenRouter");
    if (combined.includes("slack")) services.add("Slack");
    if (combined.includes("hubspot") || combined.includes("hubapi"))
      services.add("HubSpot");
    if (combined.includes("shopify")) services.add("Shopify");
    if (combined.includes("stripe")) services.add("Stripe");
    if (combined.includes("google") && combined.includes("sheet"))
      services.add("Google Sheets");
    if (combined.includes("drive")) services.add("Google Drive");
    if (combined.includes("airtable")) services.add("Airtable");
    if (combined.includes("notion")) services.add("Notion");
    if (combined.includes("twitter")) services.add("Twitter/X");
    if (combined.includes("discord")) services.add("Discord");
    if (combined.includes("sendgrid")) services.add("SendGrid");
    if (combined.includes("twilio")) services.add("Twilio");
    if (combined.includes("riverside")) services.add("Riverside");
    if (combined.includes("youtube")) services.add("YouTube");
    if (n.type === "sendEmail") services.add("Email Provider");
    if (n.type === "sendSMS") services.add("SMS (Twilio)");
  }
  return [...services];
}

// Auto-detect category from template name + description (mirrors Templates.tsx logic)
function detectCategory(name: string, desc: string): string {
  const combined = (name + " " + desc).toLowerCase();
  if (
    combined.match(
      /\b(email|newsletter|inbox|mailchimp|drip|welcome series|outreach)\b/,
    )
  )
    return "email";
  if (
    combined.match(
      /\b(sales|lead|pipeline|deal|crm|hubspot|salesforce|prospect|qualification|scoring)\b/,
    )
  )
    return "sales-crm";
  if (
    combined.match(
      /\b(marketing|campaign|social media|facebook|instagram|twitter|tiktok|ad|seo|content calendar|brand)\b/,
    )
  )
    return "marketing";
  if (
    combined.match(
      /\b(ecommerce|e-commerce|shopify|woocommerce|cart|order|product|inventory|shipping|storefront)\b/,
    )
  )
    return "ecommerce";
  if (
    combined.match(
      /\b(support|ticket|helpdesk|zendesk|customer service|freshdesk|intercom|complaint|faq)\b/,
    )
  )
    return "support";
  if (
    combined.match(
      /\b(hr|hiring|recruit|onboard|employee|pto|leave|payroll|talent|resume|candidate|interview)\b/,
    )
  )
    return "hr";
  if (
    combined.match(
      /\b(finance|invoice|payment|billing|accounting|expense|budget|quickbooks|xero|revenue)\b/,
    )
  )
    return "finance";
  if (
    combined.match(
      /\b(devops|deploy|ci\/cd|github|gitlab|monitor|incident|infrastructure|server|kubernetes)\b/,
    )
  )
    return "it-devops";
  if (
    combined.match(
      /\b(analytics|report|dashboard|data|kpi|metric|insight|chart)\b/,
    )
  )
    return "data-analytics";
  if (
    combined.match(
      /\b(video|youtube|podcast|recording|riverside|descript|media|stream)\b/,
    )
  )
    return "video";
  if (
    combined.match(
      /\b(research|competitor|market|analysis|survey|scrape|crawl|intel)\b/,
    )
  )
    return "research";
  if (
    combined.match(
      /\b(project|task|sprint|kanban|trello|asana|jira|standup|milestone)\b/,
    )
  )
    return "project-mgmt";
  if (
    combined.match(
      /\b(slack|discord|notification|message|communication|team|alert)\b/,
    )
  )
    return "communication";
  if (
    combined.match(
      /\b(document|pdf|contract|proposal|sign|form|template|google doc)\b/,
    )
  )
    return "documents";
  if (combined.match(/\b(webhook|api|integration|connect|sync|zapier)\b/))
    return "webhook-connectors";
  if (
    combined.match(/\b(operations|process|workflow|automat|schedule|batch)\b/)
  )
    return "operations";
  if (
    combined.match(
      /\b(security|compliance|audit|gdpr|hipaa|access|permission)\b/,
    )
  )
    return "security-compliance";
  if (
    combined.match(
      /\b(healthcare|patient|clinic|medical|insurance|ehr|emr|health)\b/,
    )
  )
    return "industry-specific";
  if (
    combined.match(
      /\b(education|school|student|course|learn|training|nonprofit)\b/,
    )
  )
    return "nonprofit-education";
  if (
    combined.match(
      /\b(supply chain|logistics|warehouse|shipping|fulfillment)\b/,
    )
  )
    return "supply-chain";
  if (combined.match(/\b(productivity|focus|time|reminder|habit|organize)\b/))
    return "productivity";
  return "ai-automation";
}

// Build slug → key map
function toSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 80);
}
const SLUG_MAP: Record<string, string> = {};
for (const key of Object.keys(PREBUILT_WORKFLOWS)) {
  SLUG_MAP[toSlug(key)] = key;
  SLUG_MAP[key] = key;
  const cleaned = key.replace(/^OM\d+\s+/i, "");
  if (cleaned !== key) {
    SLUG_MAP[toSlug(cleaned)] = key;
  }
}

export default function TemplateDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isLoggedIn =
    useSelector((s: any) => s.user.isLoggedIn) ||
    !!localStorage.getItem("omni-token");
  const [dbTemplate, setDbTemplate] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Also try cleaning om### prefix from slug
  const cleanSlug = slug?.replace(/^om\d+-/, "") || slug || "";
  const matchedKey = SLUG_MAP[cleanSlug] || SLUG_MAP[slug || ""] || null;
  const localWf = matchedKey
    ? (PREBUILT_WORKFLOWS as Record<string, PrebuiltWorkflow>)[matchedKey]
    : null;

  useEffect(() => {
    if (localWf) {
      setLoading(false);
      return;
    }
    if (slug) {
      axiosGet(`/templates/detail/${slug}`)
        .then((res: any) => {
          if (res.data) setDbTemplate(res.data);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [slug, !!localWf]);

  const templateName =
    matchedKey ||
    dbTemplate?.name ||
    cleanSlug
      .replace(/-/g, " ")
      .replace(/\b\w/g, (c: string) => c.toUpperCase());
  const summary =
    localWf?.summary ||
    dbTemplate?.description ||
    dbTemplate?.longDescription ||
    "";
  const rawDescription = dbTemplate?.description || "";
  const howItWorks = dbTemplate?.howItWorks || [];
  const useCases = dbTemplate?.useCases || [];
  const tags = dbTemplate?.tags || dbTemplate?.requiredConnections || [];
  const requiredConnections = dbTemplate?.requiredConnections || [];

  // Normalize nodes from both formats
  const rawNodes = localWf?.nodes || dbTemplate?.nodes || [];
  const nodes = rawNodes.map((n: any) => {
    if (n.data?.nodeLabel) return n;
    return {
      id: n.id,
      type: n.type,
      data: {
        nodeLabel: n.title || n.name || n.type,
        hitApiDetails: n.config
          ? {
              url: n.config.url,
              method: n.config.method,
              variables: n.config.outputVariables,
            }
          : undefined,
      },
    };
  });
  const edges = localWf?.edges || [];

  // Parse summary into sections
  const summaryClean = summary.replace(/\*\*/g, "");
  const summaryLines = summaryClean.split("\n").filter((l: string) => l.trim());
  const steps = summaryLines.filter((l: string) => /^\d+\./.test(l.trim()));
  const activateLine = summaryLines.find((l: string) =>
    /activate|connect|to get started/i.test(l),
  );

  // Detect services
  const services = useMemo(() => detectServices(nodes), [nodes]);

  // Generate SEO-friendly description from node data
  const description = useMemo(() => {
    if (rawDescription) return rawDescription;
    if (nodes.length === 0)
      return `Automate ${templateName} with OmniEngage. Build and customize this workflow in minutes.`;
    const nodeLabels = nodes
      .map((n: any) => n.data?.nodeLabel || "")
      .filter(Boolean);
    const trigger = nodes[0]?.data?.nodeLabel || "an event";
    const lastAction =
      nodes[nodes.length - 1]?.data?.nodeLabel || "completing the process";
    return `This ${nodes.length}-step workflow starts with ${trigger} and ends by ${lastAction.toLowerCase()}. Steps include: ${nodeLabels.slice(1, -1).join(", ")}. Fully customizable — edit any step, add new ones, or connect your own services.`;
  }, [rawDescription, nodes, templateName]);

  // SEO: set document title and meta description
  useEffect(() => {
    if (templateName) {
      document.title = `${templateName} — Workflow Template | OmniEngage`;
      let metaDesc = document.querySelector('meta[name="description"]');
      if (!metaDesc) {
        metaDesc = document.createElement("meta");
        metaDesc.setAttribute("name", "description");
        document.head.appendChild(metaDesc);
      }
      metaDesc.setAttribute(
        "content",
        `${description.slice(0, 155)}. Automate with OmniEngage — use this template free.`,
      );
    }
    return () => {
      document.title = "OmniEngage";
    };
  }, [templateName, description]);

  // SEO: Canonical tag
  useEffect(() => {
    if (!slug) return;
    let link = document.querySelector(
      'link[rel="canonical"]',
    ) as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", `https://omniengage.co/templates/${slug}`);
    return () => {
      const el = document.querySelector('link[rel="canonical"]');
      if (el) el.remove();
    };
  }, [slug]);

  // Detect category for this template
  const templateCategory = useMemo(() => {
    return detectCategory(templateName, summary);
  }, [templateName, summary]);

  // Related templates: find 4 templates from the same category
  const relatedTemplates = useMemo(() => {
    const allEntries = Object.entries(
      PREBUILT_WORKFLOWS as Record<string, PrebuiltWorkflow>,
    );
    const categorized = allEntries
      .filter(([name]) => name && name.length >= 3 && !/^\d+$/.test(name))
      .map(([name, wf]) => ({
        name,
        slug: toSlug(name),
        category: detectCategory(name, (wf as any).summary || ""),
        nodeCount: (wf as any).nodes?.length || 0,
      }))
      .filter(
        (t) => t.slug !== cleanSlug && t.slug !== slug && t.name !== matchedKey,
      );

    // Get same-category templates first, then fill with others
    const sameCategory = categorized.filter(
      (t) => t.category === templateCategory,
    );
    const others = categorized.filter((t) => t.category !== templateCategory);
    const pool = [...sameCategory, ...others];
    return pool.slice(0, 4);
  }, [templateCategory, cleanSlug, slug, matchedKey]);

  // FAQ items
  const faqItems = useMemo(() => {
    const servicesList =
      services.length > 0
        ? services.join(", ")
        : "No external services required — this template works out of the box.";
    return [
      {
        question: `What does the ${templateName} template do?`,
        answer: description,
      },
      {
        question: "How do I use this template?",
        answer:
          "Click Use Template to load it into the visual builder. The AI assistant guides you through connecting services and customizing each step.",
      },
      {
        question: "Is this template free?",
        answer:
          "Yes, all templates are free to use. You can customize any step and connect your own services.",
      },
      {
        question: "Can I customize this workflow?",
        answer:
          "Every step is fully customizable. Edit nodes directly or use the AI chat to make changes in plain English.",
      },
      {
        question: `What services does this template need?`,
        answer: servicesList,
      },
      {
        question: "How long does it take to set up?",
        answer:
          "Most templates can be configured in under 10 minutes. The AI assistant walks you through each connection step by step.",
      },
    ];
  }, [templateName, description, services]);

  // SEO: JSON-LD structured data (SoftwareApplication + BreadcrumbList + FAQPage)
  useEffect(() => {
    if (!templateName || !slug) return;

    const jsonLd = [
      {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: templateName,
        description: description.slice(0, 300),
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        offers: {
          "@type": "Offer",
          price: "0",
          priceCurrency: "USD",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://omniengage.co",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Templates",
            item: "https://omniengage.co/templates",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: templateName,
            item: `https://omniengage.co/templates/${slug}`,
          },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqItems.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: item.answer,
          },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    return () => {
      script.remove();
    };
  }, [templateName, slug, description, faqItems]);

  // Node type stats
  const typeCounts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const n of nodes) {
      c[n.type] = (c[n.type] || 0) + 1;
    }
    return c;
  }, [nodes]);

  // Generate "how to edit" tips based on node types
  const editTips = useMemo(() => {
    const tips: string[] = [];
    if (typeCounts.hitApi)
      tips.push(
        "Click any API node to change the endpoint, headers, or output variables",
      );
    if (typeCounts.sendEmail)
      tips.push(
        'Edit email subject and body by clicking the email node, or say "change email subject" in chat',
      );
    if (typeCounts.condition)
      tips.push(
        "Modify condition logic by clicking the condition node — change the variable, operator, or value",
      );
    if (typeCounts.runCode)
      tips.push(
        "Edit JavaScript code by clicking the code node — use input.text or input.message to access data",
      );
    if (typeCounts.delayAction)
      tips.push(
        "Adjust wait times by clicking the delay node — change duration and unit",
      );
    if (typeCounts.approvalGate)
      tips.push(
        "Customize the approval message and add reviewers in the approval node",
      );
    tips.push(
      'Add new steps by saying "add a delay after step 3" or "add an approval before the last step" in chat',
    );
    tips.push('Remove steps by saying "remove step 4" in chat');
    tips.push(
      'Rearrange by saying "move the email step before the condition" in chat',
    );
    return tips;
  }, [typeCounts]);

  // Generate setup checklist
  const setupSteps = useMemo(() => {
    const s: string[] = [];
    const allServices = [
      ...services,
      ...requiredConnections.map(
        (r: string) => r.charAt(0).toUpperCase() + r.slice(1),
      ),
    ];
    const unique = [...new Set(allServices)];
    if (unique.length > 0)
      s.push(`Connect your services: ${unique.join(", ")}`);
    if (typeCounts.sendEmail)
      s.push("Set up your email provider (Gmail, SendGrid, etc.)");
    if (typeCounts.campaign)
      s.push(
        "Configure how the workflow triggers (webhook, schedule, or manual)",
      );
    s.push("Review and customize each step for your specific use case");
    s.push("Test the workflow with sample data before going live");
    s.push('Publish when ready — say "go live" in the chat');
    return s;
  }, [services, requiredConnections, typeCounts]);

  function handleUse() {
    if (!isLoggedIn) {
      dispatch(setTextToWorkflowPrompt(templateName));
      navigate("/login", {
        state: {
          prevPath: `/text-to-workflow?prompt=${encodeURIComponent(templateName)}`,
        },
      });
      return;
    }
    if (localWf && matchedKey) {
      dispatch(setPrebuiltWorkflow(localWf));
      dispatch(setTextToWorkflowPrompt(matchedKey));
    } else {
      dispatch(setTextToWorkflowPrompt(templateName));
    }
    navigate("/text-to-workflow");
  }

  if (!slug)
    return (
      <div style={{ padding: 40, textAlign: "center", fontFamily: T.font }}>
        Template not found
      </div>
    );
  if (loading)
    return (
      <div
        style={{
          minHeight: "100vh",
          background: T.bg,
          fontFamily: T.font,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: 32,
              height: 32,
              border: `3px solid ${T.border}`,
              borderTopColor: T.coral,
              borderRadius: "50%",
              animation: "spin 0.8s linear infinite",
              margin: "0 auto 12px",
            }}
          />
          <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
          <p style={{ color: T.textMuted, fontSize: 14 }}>
            Loading template...
          </p>
        </div>
      </div>
    );

  return (
    <div style={{ minHeight: "100vh", background: T.bg, fontFamily: T.font }}>
      {/* Sticky nav */}
      <div
        style={{
          background: "#fff",
          borderBottom: "1px solid #eee",
          padding: "0 24px",
          position: "sticky",
          top: 0,
          zIndex: 50,
        }}
      >
        <div
          style={{
            maxWidth: 960,
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            height: 56,
          }}
        >
          <button
            onClick={() => navigate("/templates")}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: 14,
              color: T.textSecondary,
            }}
          >
            ← Back to Templates
          </button>
          <button
            onClick={handleUse}
            style={{
              padding: "8px 24px",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              background: `linear-gradient(90deg, ${T.coral}, ${T.coralDark})`,
              color: "#fff",
              border: "none",
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(227,35,44,0.2)",
            }}
          >
            Use This Template
          </button>
        </div>
      </div>

      <div
        style={{ maxWidth: 960, margin: "0 auto", padding: "40px 24px 80px" }}
      >
        {/* ═══ HEADER ═══ */}
        <h1
          style={{
            fontSize: 30,
            fontWeight: 700,
            color: T.text,
            margin: "0 0 10px",
            lineHeight: 1.3,
          }}
        >
          {templateName}
        </h1>
        <p
          style={{
            fontSize: 15,
            color: T.textSecondary,
            margin: "0 0 20px",
            lineHeight: 1.7,
            maxWidth: 700,
          }}
        >
          {description ||
            summaryLines[0] ||
            "Automate this workflow with AI-powered steps."}
        </p>

        {/* Tags + services */}
        <div
          style={{
            display: "flex",
            gap: 6,
            flexWrap: "wrap",
            marginBottom: 28,
          }}
        >
          {tags.map((tag: string, i: number) => (
            <span
              key={i}
              style={{
                fontSize: 11,
                padding: "3px 10px",
                borderRadius: 20,
                background: "#f3f4f6",
                color: T.textSecondary,
                fontWeight: 500,
              }}
            >
              {tag}
            </span>
          ))}
          {services.map((svc, i) => (
            <span
              key={`svc-${i}`}
              style={{
                fontSize: 11,
                padding: "3px 10px",
                borderRadius: 20,
                background: "#eff6ff",
                color: "#2563eb",
                fontWeight: 500,
                border: "1px solid #bfdbfe",
              }}
            >
              {svc}
            </span>
          ))}
        </div>

        {/* ═══ STATS ═══ */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
            gap: 12,
            marginBottom: 36,
          }}
        >
          {[
            { value: nodes.length, label: "Steps", color: T.coral },
            {
              value: edges.length || nodes.length - 1,
              label: "Connections",
              color: "#2563eb",
            },
            {
              value: Object.keys(typeCounts).length,
              label: "Node Types",
              color: "#16a34a",
            },
            { value: services.length, label: "Services", color: "#7c3aed" },
          ].map((s, i) => (
            <div
              key={i}
              style={{
                padding: "16px 20px",
                borderRadius: 12,
                background: "#fff",
                border: "1px solid #eef0f4",
              }}
            >
              <div style={{ fontSize: 24, fontWeight: 700, color: s.color }}>
                {s.value}
              </div>
              <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>
                {s.label}
              </div>
            </div>
          ))}
        </div>

        {/* ═══ OVERVIEW — auto-generated rich text for SEO ═══ */}
        <section style={{ marginBottom: 32 }}>
          <h2
            style={{
              fontSize: 18,
              fontWeight: 600,
              color: T.text,
              margin: "0 0 14px",
            }}
          >
            Overview
          </h2>
          <div
            style={{
              background: "#fff",
              borderRadius: 12,
              border: "1px solid #eef0f4",
              padding: "20px 24px",
              fontSize: 14,
              color: T.text,
              lineHeight: 1.8,
            }}
          >
            <p style={{ margin: "0 0 12px" }}>
              The <strong>{templateName}</strong> template automates a{" "}
              {nodes.length}-step workflow
              {services.length > 0 ? ` using ${services.join(", ")}` : ""}.
              {nodes[0]?.type === "campaign"
                ? ` It triggers ${nodes[0]?.data?.nodeLabel?.toLowerCase()?.includes("schedule") ? "on a schedule" : nodes[0]?.data?.nodeLabel?.toLowerCase()?.includes("webhook") ? "via webhook when an external event fires" : nodes[0]?.data?.nodeLabel?.toLowerCase()?.includes("manual") ? "manually when you run it" : "automatically when the configured event occurs"}.`
                : ""}
            </p>
            {nodes.length > 2 && (
              <p style={{ margin: "0 0 12px" }}>
                The workflow processes data through {nodes.length - 1} steps:{" "}
                {nodes
                  .slice(1)
                  .map((n: any) => n.data?.nodeLabel || n.type)
                  .join(", ")}
                .
                {typeCounts.condition
                  ? ` It includes ${typeCounts.condition} decision point${typeCounts.condition > 1 ? "s" : ""} that route data based on conditions you define.`
                  : ""}
                {typeCounts.hitApi
                  ? ` ${typeCounts.hitApi} API call${typeCounts.hitApi > 1 ? "s" : ""} connect${typeCounts.hitApi === 1 ? "s" : ""} to external services.`
                  : ""}
                {typeCounts.sendEmail
                  ? ` ${typeCounts.sendEmail} email${typeCounts.sendEmail > 1 ? "s are" : " is"} sent automatically.`
                  : ""}
              </p>
            )}
            <p style={{ margin: "0 0 12px" }}>
              Every step is fully customizable. Click any node to edit its
              configuration, or use the AI chat assistant to make changes in
              plain English — for example, "change the condition threshold to
              50" or "add a Slack notification after the email."
            </p>
            <p style={{ margin: 0 }}>
              To get started, click <strong>Use This Template</strong> to load
              it into the visual builder. The AI assistant will guide you
              through connecting your services and customizing each step for
              your specific needs.
              {services.length > 0
                ? ` You'll need to connect: ${services.join(", ")}.`
                : ""}
            </p>
          </div>
        </section>

        {/* ═══ TWO-COLUMN LAYOUT ═══ */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 340px",
            gap: 24,
            alignItems: "start",
          }}
        >
          {/* LEFT — main content */}
          <div>
            {/* How it works */}
            {(steps.length > 0 || howItWorks.length > 0) && (
              <section style={{ marginBottom: 32 }}>
                <h2
                  style={{
                    fontSize: 18,
                    fontWeight: 600,
                    color: T.text,
                    margin: "0 0 14px",
                  }}
                >
                  How It Works
                </h2>
                <div
                  style={{
                    background: "#fff",
                    borderRadius: 12,
                    border: "1px solid #eef0f4",
                    padding: "16px 20px",
                  }}
                >
                  {(steps.length > 0 ? steps : howItWorks).map(
                    (step: string, i: number) => (
                      <div
                        key={i}
                        style={{
                          display: "flex",
                          gap: 12,
                          alignItems: "flex-start",
                          padding: "10px 0",
                          borderBottom:
                            i < (steps.length || howItWorks.length) - 1
                              ? "1px solid #f3f4f6"
                              : "none",
                        }}
                      >
                        <div
                          style={{
                            width: 26,
                            height: 26,
                            borderRadius: "50%",
                            background: `linear-gradient(135deg, ${T.coral}, ${T.coralDark})`,
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 12,
                            fontWeight: 600,
                            flexShrink: 0,
                            marginTop: 1,
                          }}
                        >
                          {i + 1}
                        </div>
                        <div
                          style={{
                            fontSize: 14,
                            color: T.text,
                            lineHeight: 1.5,
                          }}
                        >
                          {typeof step === "string"
                            ? step.replace(/^\d+\.\s*/, "")
                            : step}
                        </div>
                      </div>
                    ),
                  )}
                </div>
              </section>
            )}

            {/* Workflow steps — detailed */}
            <section style={{ marginBottom: 32 }}>
              <h2
                style={{
                  fontSize: 18,
                  fontWeight: 600,
                  color: T.text,
                  margin: "0 0 14px",
                }}
              >
                Workflow Steps
              </h2>
              <div
                style={{
                  background: "#fff",
                  borderRadius: 12,
                  border: "1px solid #eef0f4",
                  overflow: "hidden",
                }}
              >
                {nodes.map((node: any, i: number) => {
                  const meta = NODE_META[node.type] || {
                    icon: "📦",
                    label: node.type,
                    desc: "Workflow step",
                  };
                  const label = node.data?.nodeLabel || node.title || node.type;
                  const url = node.data?.hitApiDetails?.url || "";
                  const method = node.data?.hitApiDetails?.method || "";
                  const variables = node.data?.hitApiDetails?.variables || [];
                  const hasCode =
                    node.data?.code || node.data?.runCodeDetails?.code;
                  const emailSubject =
                    node.data?.sendEmailDetails?.content?.subject;
                  const condVar = node.data?.conditionData?.[0]?.variable;
                  const delayTime = node.data?.delayAction;
                  const systemPrompt = node.data?.systemPrompt;

                  return (
                    <div
                      key={node.id || i}
                      style={{
                        padding: "14px 20px",
                        borderBottom:
                          i < nodes.length - 1 ? "1px solid #f3f4f6" : "none",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          marginBottom: 4,
                        }}
                      >
                        <div
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: 8,
                            background: "#f3f4f6",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 14,
                            flexShrink: 0,
                          }}
                        >
                          {meta.icon}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <span
                              style={{
                                fontSize: 14,
                                fontWeight: 600,
                                color: T.text,
                              }}
                            >
                              {label}
                            </span>
                            <span
                              style={{
                                fontSize: 10,
                                padding: "1px 8px",
                                borderRadius: 4,
                                background: "#f3f4f6",
                                color: T.textMuted,
                              }}
                            >
                              {meta.label}
                            </span>
                          </div>
                        </div>
                        <span
                          style={{
                            fontSize: 11,
                            color: T.textMuted,
                            fontFamily: "monospace",
                          }}
                        >
                          #{node.id}
                        </span>
                      </div>
                      {/* Node details */}
                      <div
                        style={{
                          marginLeft: 44,
                          fontSize: 12,
                          color: T.textSecondary,
                          lineHeight: 1.6,
                        }}
                      >
                        {url && (
                          <div>
                            <span style={{ color: T.textMuted }}>
                              Endpoint:
                            </span>{" "}
                            <code
                              style={{
                                background: "#f8f9fb",
                                padding: "1px 4px",
                                borderRadius: 3,
                                fontSize: 11,
                              }}
                            >
                              {method}{" "}
                              {url.length > 60 ? url.slice(0, 60) + "..." : url}
                            </code>
                          </div>
                        )}
                        {variables.length > 0 && (
                          <div>
                            <span style={{ color: T.textMuted }}>Outputs:</span>{" "}
                            {variables
                              .map((v: any) => v.customName || v.name)
                              .join(", ")}
                          </div>
                        )}
                        {emailSubject && (
                          <div>
                            <span style={{ color: T.textMuted }}>Subject:</span>{" "}
                            {emailSubject}
                          </div>
                        )}
                        {condVar && (
                          <div>
                            <span style={{ color: T.textMuted }}>Checks:</span>{" "}
                            {condVar}{" "}
                            {node.data?.conditionData?.[0]?.comparison_operator}{" "}
                            {node.data?.conditionData?.[0]?.value}
                          </div>
                        )}
                        {delayTime && (
                          <div>
                            <span style={{ color: T.textMuted }}>Waits:</span>{" "}
                            {delayTime.time} {delayTime.timeFormat}
                          </div>
                        )}
                        {systemPrompt && (
                          <div>
                            <span style={{ color: T.textMuted }}>
                              AI Prompt:
                            </span>{" "}
                            {systemPrompt.length > 80
                              ? systemPrompt.slice(0, 80) + "..."
                              : systemPrompt}
                          </div>
                        )}
                        {hasCode && (
                          <div>
                            <span style={{ color: T.textMuted }}>Code:</span>{" "}
                            Custom JavaScript logic
                          </div>
                        )}
                        {!url &&
                          !emailSubject &&
                          !condVar &&
                          !delayTime &&
                          !hasCode &&
                          !systemPrompt && (
                            <div style={{ color: T.textMuted }}>
                              {meta.desc}
                            </div>
                          )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Use cases */}
            {useCases.length > 0 && (
              <section style={{ marginBottom: 32 }}>
                <h2
                  style={{
                    fontSize: 18,
                    fontWeight: 600,
                    color: T.text,
                    margin: "0 0 14px",
                  }}
                >
                  Use Cases
                </h2>
                <div
                  style={{
                    background: "#fff",
                    borderRadius: 12,
                    border: "1px solid #eef0f4",
                    padding: "16px 20px",
                  }}
                >
                  {useCases.map((uc: string, i: number) => (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        gap: 8,
                        padding: "6px 0",
                        fontSize: 13,
                        color: T.text,
                      }}
                    >
                      <span style={{ color: T.coral, flexShrink: 0 }}>-</span>{" "}
                      {uc}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* How to edit */}
            <section style={{ marginBottom: 32 }}>
              <h2
                style={{
                  fontSize: 18,
                  fontWeight: 600,
                  color: T.text,
                  margin: "0 0 14px",
                }}
              >
                How to Customize
              </h2>
              <div
                style={{
                  background: "#fff",
                  borderRadius: 12,
                  border: "1px solid #eef0f4",
                  padding: "16px 20px",
                }}
              >
                {editTips.map((tip, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      gap: 10,
                      padding: "8px 0",
                      borderBottom:
                        i < editTips.length - 1 ? "1px solid #f8f9fb" : "none",
                    }}
                  >
                    <span
                      style={{ color: "#2563eb", flexShrink: 0, fontSize: 12 }}
                    >
                      Tip {i + 1}
                    </span>
                    <span
                      style={{ fontSize: 13, color: T.text, lineHeight: 1.5 }}
                    >
                      {tip}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          </div>

          {/* RIGHT — sidebar */}
          <div style={{ position: "sticky", top: 80 }}>
            {/* Use template card */}
            <div
              style={{
                background: "#fff",
                borderRadius: 12,
                border: "1px solid #eef0f4",
                padding: "24px 20px",
                marginBottom: 16,
              }}
            >
              <button
                onClick={handleUse}
                style={{
                  width: "100%",
                  padding: "12px 0",
                  borderRadius: 10,
                  fontSize: 14,
                  fontWeight: 600,
                  background: `linear-gradient(90deg, ${T.coral}, ${T.coralDark})`,
                  color: "#fff",
                  border: "none",
                  cursor: "pointer",
                  boxShadow: "0 4px 12px rgba(227,35,44,0.2)",
                  marginBottom: 12,
                }}
              >
                Use This Template
              </button>
              <button
                onClick={() => navigate("/text-to-workflow")}
                style={{
                  width: "100%",
                  padding: "10px 0",
                  borderRadius: 10,
                  fontSize: 13,
                  fontWeight: 500,
                  background: "#f8f9fb",
                  color: T.textSecondary,
                  border: "1px solid #e5e7eb",
                  cursor: "pointer",
                }}
              >
                Build Custom Instead
              </button>
            </div>

            {/* Setup checklist */}
            <div
              style={{
                background: "#fff",
                borderRadius: 12,
                border: "1px solid #eef0f4",
                padding: "20px",
                marginBottom: 16,
              }}
            >
              <h3
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: T.text,
                  margin: "0 0 12px",
                }}
              >
                Setup Checklist
              </h3>
              {setupSteps.map((step, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "flex-start",
                    padding: "6px 0",
                    fontSize: 12,
                    color: T.textSecondary,
                    lineHeight: 1.5,
                  }}
                >
                  <span
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: 4,
                      border: "1.5px solid #d1d5db",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      marginTop: 1,
                      fontSize: 10,
                    }}
                  >
                    {i + 1}
                  </span>
                  {step}
                </div>
              ))}
            </div>

            {/* Node types */}
            <div
              style={{
                background: "#fff",
                borderRadius: 12,
                border: "1px solid #eef0f4",
                padding: "20px",
              }}
            >
              <h3
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: T.text,
                  margin: "0 0 12px",
                }}
              >
                Node Types Used
              </h3>
              {Object.entries(typeCounts).map(([type, count]) => {
                const meta = NODE_META[type] || {
                  icon: "📦",
                  label: type,
                  desc: "",
                };
                return (
                  <div
                    key={type}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "5px 0",
                      fontSize: 12,
                    }}
                  >
                    <span>{meta.icon}</span>
                    <span style={{ flex: 1, color: T.text, fontWeight: 500 }}>
                      {meta.label}
                    </span>
                    <span style={{ color: T.textMuted }}>x{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ═══ SETUP REQUIREMENTS ═══ */}
        {(activateLine || requiredConnections.length > 0) && (
          <section style={{ marginTop: 8, marginBottom: 32 }}>
            <div
              style={{
                background: "#fffbeb",
                borderRadius: 12,
                border: "1px solid #fde68a",
                padding: "16px 20px",
              }}
            >
              <h3
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#92400e",
                  margin: "0 0 8px",
                }}
              >
                Before Going Live
              </h3>
              <p
                style={{
                  fontSize: 13,
                  color: "#92400e",
                  lineHeight: 1.6,
                  margin: 0,
                }}
              >
                {activateLine
                  ? activateLine.replace(/^To activate:\s*/i, "")
                  : `Connect these services: ${requiredConnections.join(", ")}. The AI chat will walk you through each connection step by step.`}
              </p>
            </div>
          </section>
        )}

        {/* ═══ FAQ SECTION ═══ */}
        <section style={{ marginTop: 8, marginBottom: 32 }}>
          <h2
            style={{
              fontSize: 20,
              fontWeight: 700,
              color: T.text,
              margin: "0 0 16px",
            }}
          >
            Frequently Asked Questions
          </h2>
          <style>{`
            .td-faq-details summary { cursor: pointer; list-style: none; }
            .td-faq-details summary::-webkit-details-marker { display: none; }
            .td-faq-details summary::marker { display: none; content: ""; }
            .td-faq-details[open] .td-faq-chevron { transform: rotate(180deg); }
            .td-faq-details .td-faq-answer { overflow: hidden; max-height: 0; transition: max-height 0.3s ease, padding 0.3s ease; padding: 0 20px; }
            .td-faq-details[open] .td-faq-answer { max-height: 500px; padding: 0 20px 16px; }
          `}</style>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {faqItems.map((item, i) => (
              <details
                key={i}
                className="td-faq-details"
                style={{
                  background: "#fff",
                  borderRadius: 10,
                  border: "1px solid #eef0f4",
                  overflow: "hidden",
                }}
              >
                <summary
                  style={{
                    padding: "14px 20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    fontSize: 14,
                    fontWeight: 600,
                    color: T.text,
                    lineHeight: 1.5,
                    userSelect: "none",
                    WebkitUserSelect: "none",
                  }}
                >
                  <span style={{ flex: 1, paddingRight: 12 }}>
                    {item.question}
                  </span>
                  <span
                    className="td-faq-chevron"
                    style={{
                      fontSize: 12,
                      color: T.textMuted,
                      transition: "transform 0.3s ease",
                      flexShrink: 0,
                    }}
                  >
                    &#9660;
                  </span>
                </summary>
                <div className="td-faq-answer">
                  <p
                    style={{
                      fontSize: 13,
                      color: T.textSecondary,
                      lineHeight: 1.7,
                      margin: 0,
                      borderTop: "1px solid #f3f4f6",
                      paddingTop: 12,
                    }}
                  >
                    {item.answer}
                  </p>
                </div>
              </details>
            ))}
          </div>
        </section>

        {/* ═══ RELATED TEMPLATES ═══ */}
        {relatedTemplates.length > 0 && (
          <section style={{ marginBottom: 32 }}>
            <h2
              style={{
                fontSize: 20,
                fontWeight: 700,
                color: T.text,
                margin: "0 0 16px",
              }}
            >
              Related Templates
            </h2>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))",
                gap: 16,
              }}
            >
              {relatedTemplates.map((rt) => (
                <div
                  key={rt.slug}
                  style={{
                    background: "#fff",
                    borderRadius: 12,
                    border: "1px solid #eef0f4",
                    overflow: "hidden",
                    transition: "box-shadow 0.2s ease, border-color 0.2s ease",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <div
                    style={{
                      height: 80,
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        opacity: 0.06,
                        backgroundImage:
                          "radial-gradient(circle, #fff 1px, transparent 1px)",
                        backgroundSize: "16px 16px",
                      }}
                    />
                    <span
                      style={{
                        fontSize: 12,
                        color: "rgba(255,255,255,0.9)",
                        fontWeight: 600,
                        zIndex: 1,
                        padding: "0 12px",
                        textAlign: "center",
                        lineHeight: 1.3,
                      }}
                    >
                      {rt.nodeCount} steps
                    </span>
                  </div>
                  <div
                    style={{
                      padding: "14px 16px",
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Link
                      to={`/templates/${rt.slug}`}
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: T.text,
                        textDecoration: "none",
                        lineHeight: 1.4,
                        marginBottom: 10,
                        display: "block",
                      }}
                    >
                      {rt.name}
                    </Link>
                    <div style={{ marginTop: "auto" }}>
                      <Link
                        to={`/templates/${rt.slug}`}
                        style={{
                          display: "block",
                          textAlign: "center",
                          padding: "7px 0",
                          borderRadius: 8,
                          fontSize: 12,
                          fontWeight: 600,
                          textDecoration: "none",
                          background: `linear-gradient(90deg, ${T.coral}, ${T.coralDark})`,
                          color: "#fff",
                        }}
                      >
                        Use Template
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ═══ BOTTOM CTA ═══ */}
        <div
          style={{
            background: "#1a1a2e",
            borderRadius: 16,
            padding: "48px 32px",
            textAlign: "center",
            marginTop: 16,
          }}
        >
          <h2
            style={{
              fontSize: 24,
              fontWeight: 700,
              color: "#fff",
              margin: "0 0 8px",
            }}
          >
            Ready to automate?
          </h2>
          <p
            style={{
              fontSize: 14,
              color: "#94a3b8",
              margin: "0 0 24px",
              maxWidth: 500,
              marginInline: "auto",
            }}
          >
            Load this template into the builder. The AI assistant will help you
            customize every step, connect your services, and go live.
          </p>
          <div
            style={{
              display: "flex",
              gap: 12,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <button
              onClick={handleUse}
              style={{
                padding: "12px 32px",
                borderRadius: 10,
                fontSize: 14,
                fontWeight: 600,
                background: `linear-gradient(90deg, ${T.coral}, ${T.coralDark})`,
                color: "#fff",
                border: "none",
                cursor: "pointer",
                boxShadow: "0 4px 16px rgba(251,95,102,0.3)",
              }}
            >
              Use This Template
            </button>
            <button
              onClick={() => navigate("/templates")}
              style={{
                padding: "12px 32px",
                borderRadius: 10,
                fontSize: 14,
                fontWeight: 500,
                background: "rgba(255,255,255,0.1)",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.2)",
                cursor: "pointer",
              }}
            >
              Browse More Templates
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
